'use client';

import { Typography } from '@/components/ui/Text';
import type { SalesforceLocationResponse } from '@/types/salesforce';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useState } from 'react';
import { useIntakeWizardContext } from '../../contexts/WizardProvider';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { IntakePageTextBox } from '../common/IntakePageTextBox';
import { IntakeStepWrapper } from '../common/IntakeStepWrapper';
import { TextInput } from '../common/TextInput';

const isValidZipCode = (zip: string): boolean => /^\d{5}(-\d{4})?$/.test(zip);

const fetchLocationByZipCode = async (
  zipCode: string
): Promise<SalesforceLocationResponse | null> => {
  const response = await fetch(
    `/api/lookup-location?zipCode=${encodeURIComponent(zipCode)}`
  );

  if (response.status === StatusCodes.NOT_FOUND) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to look up location');
  }

  return response.json();
};

export const ZipCodeStep: React.FC = () => {
  const { state, dispatch } = useIntakeWizardContext();
  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();

  const [zipCode, setZipCode] = useState(state.formData?.zip_code || '');
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (error) {
        setError(undefined);
      }

      setZipCode(event.target.value);
    },
    [error]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedZip = zipCode.trim();

      if (!trimmedZip) {
        setError('ZIP code is required');
        return;
      }

      if (!isValidZipCode(trimmedZip)) {
        setError('Please enter a valid US ZIP code');
        return;
      }

      setIsLoading(true);

      setError(undefined);

      try {
        const locationData = await fetchLocationByZipCode(trimmedZip);

        if (locationData) {
          dispatch({
            type: 'SET_FORM_DATA',
            payload: {
              zip_code: trimmedZip,
              location_has_pediatrics: locationData.hasPediatrics,
              nearest_location: {
                locationName: locationData.locationName,
                marketName: locationData.marketName,
                salesforceName: locationData.salesforceName,
                alternativeLocations: locationData.alternativeLocations,
              },
            },
          });
          return;
        }

        if (!locationData) {
          dispatch({
            type: 'SET_FORM_DATA',
            payload: {
              zip_code: trimmedZip,
              location_has_pediatrics: false,
              nearest_location: undefined,
            },
          });

          return;
        }
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : 'Failed to look up location. Please try again.'
        );
      } finally {
        setIsLoading(false);

        goToNextStep();
      }
    },
    [zipCode, dispatch, goToNextStep]
  );

  return (
    <IntakeStepWrapper
      heading='Where do you live?'
      description='Please enter your primary ZIP code to find your nearest Sollis center.'
      onSubmit={handleSubmit}
      onBack={goToPreviousStep}
      showBack={hasPreviousStep}
      isSubmitting={isLoading}
    >
      <div>
        {error && (
          <IntakePageTextBox align='center'>
            <Typography
              className='mt-1 text-sm text-red-500'
              variant='body-small'
            >
              {error}
            </Typography>
          </IntakePageTextBox>
        )}

        <TextInput
          type='text'
          id='zip_code'
          name='zip_code'
          value={zipCode}
          onChange={handleChange}
          error={error}
          placeholder='ZIP code'
          maxLength={10}
          inputMode='numeric'
          disabled={isLoading}
        />

        <IntakePageTextBox align='center'>
          <Typography variant='body-small'>
            Sollis has centers in Manhattan, the Hamptons, Los Angeles, the Bay
            Area, and South Florida.
          </Typography>
        </IntakePageTextBox>
      </div>
    </IntakeStepWrapper>
  );
};
